/*
 * Shopify - Automatic Cart Updates
 * - Detects quantity changes on the cart page.
 * - Sends updates to Shopify via AJAX.
 * - Updates line item prices and cart subtotal without a page reload.
 */
document.addEventListener('DOMContentLoaded', () => {
  // Select the main cart container. If it's not on the page, the script does nothing.
  const cartContainer = document.querySelector('form[action="/cart"]');
  if (!cartContainer) {
    return; // Exit if we're not on the cart page
  }

  // --- DEBOUNCE FUNCTION ---
  // This prevents the code from firing on every single keystroke, which can
  // spam Shopify's API. It waits until the user has stopped typing for a
  // set time (e.g., 500 milliseconds) before running the update.
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  // --- FUNCTION TO HANDLE THE UPDATE ---
  const handleQuantityChange = (event) => {
    const input = event.target;
    const cartItem = input.closest('.cart-item'); // Find the parent cart item row
    if (!cartItem) return;

    // Get the necessary data from the HTML
    const key = input.getAttribute('data-cart-item-key');
    const newQuantity = parseInt(input.value, 10);

    // If quantity is invalid or missing key, do nothing
    if (isNaN(newQuantity) || !key) return;
    
    // Show a loading indicator to the user
    cartContainer.classList.add('cart--loading');

    // Prepare the data for Shopify's AJAX API
    const requestBody = {
      line: getLineItemNumber(input), // Or use id: key, but line is also reliable
      quantity: newQuantity,
    };

    // Send the request to Shopify
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => response.json())
    .then(cart => {
      // Shopify returned the updated cart. Now update the page.
      if (cart.status === 422) {
        // Handle errors, like not enough stock
        alert(cart.description);
        // Revert the quantity input to the original value from the returned cart data
        const itemInCart = cart.items.find(item => item.key === key);
        if (itemInCart) {
            input.value = itemInCart.quantity;
        }
      } else {
        // Update was successful, so update the prices
        updatePageWithCartData(cart);
      }
    })
    .catch((error) => {
      console.error('Error updating cart:', error);
      alert('There was an error updating your cart. Please try again.');
    })
    .finally(() => {
      // Remove the loading indicator
      cartContainer.classList.remove('cart--loading');
    });
  };

  // --- HELPER FUNCTION to find the 'line' number ---
  const getLineItemNumber = (input) => {
    const cartItems = Array.from(document.querySelectorAll('.cart-item'));
    const currentItem = input.closest('.cart-item');
    return cartItems.indexOf(currentItem) + 1; // Line items are 1-indexed
  }

  // --- HELPER FUNCTION to update the page HTML ---
  const updatePageWithCartData = (cart) => {
    // Update the main cart total
    const subtotalElement = document.querySelector('.cart-footer__totals strong');
    if (subtotalElement) {
      subtotalElement.innerHTML = formatMoney(cart.total_price);
    }

    // Update individual line item prices
    cart.items.forEach(item => {
      const lineItemElement = document.querySelector(`[data-cart-item-key="${item.key}"]`);
      if (lineItemElement) {
        const itemRow = lineItemElement.closest('.cart-item');
        const priceElement = itemRow.querySelector('.cart-item__price-wrapper');
        if (priceElement) {
          // If the item quantity is 0, the theme might remove the row, but if not, clear the price.
          if (item.quantity === 0) {
              itemRow.remove(); // Or hide it
          } else {
              priceElement.innerHTML = formatMoney(item.final_line_price);
          }
        }
      }
    });
  };

  // --- HELPER FUNCTION to format money (respects Shopify's currency settings) ---
  const formatMoney = (cents) => {
    // This is a basic formatter. Your theme might have a global Shopify.formatMoney function
    // that is more robust. If it exists, we use it.
    if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    // Fallback basic formatter
    const dollars = (cents / 100).toFixed(2);
    // You might need to adjust the currency symbol and format based on your store's setup.
    return `$${dollars} USD`;
  };

  // --- ADD EVENT LISTENERS ---
  // Create a debounced version of our handler
  const debouncedChangeHandler = debounce(handleQuantityChange, 500);

  cartContainer.addEventListener('change', (event) => {
    // We only care about changes to quantity inputs
    if (event.target.matches('input[name="updates[]"]')) {
      debouncedChangeHandler(event);
    }
  });

  // Also listen for 'click' on the +/- buttons if they don't trigger a 'change' event immediately
  cartContainer.addEventListener('click', (event) => {
      if (event.target.matches('.quantity__button')) {
        // Find the associated input to trigger the change logic
        const input = event.target.parentElement.querySelector('input[name="updates[]"]');
        if (input) {
            // We need to create a new event because the input value was changed programmatically
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
        }
    }
  });
});

// Add some basic CSS for the loading state
const style = document.createElement('style');
style.innerHTML = `
  .cart--loading {
    opacity: 0.6;
    pointer-events: none;
    position: relative;
  }
  .cart--loading::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    margin-top: -20px;
    margin-left: -20px;
    border: 4px solid rgba(0,0,0,0.1);
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    z-index: 10;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);